import {Slider, SliderFilledTrack, SliderMark, SliderThumb, SliderTrack, useMediaQuery} from '@chakra-ui/react';
import {unwrapResult} from '@reduxjs/toolkit';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {
  Col,
  UiBox,
  UiButton,
  UiDivider,
  UiImage,
  UiQuestionnaireLayout,
  UiSimpleGrid,
  UiText,
  UiTextarea,
} from '~/components/elements';
import {AppCarousel} from '~/components/elements/AppCarousel';
import {UiCard} from '~/components/elements/UiCard';
import {QUERY_MOBILE} from '~/constants';
import {METHOD_KEYS, STI_TESTING_KEYS} from '~/constants/medical-questionnaires';
import {useInput, useNotification, useRequestState} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer, setUserQuestionnaireId} from '~/redux/questionnaires/slice';
import {addQuestionnaire} from '~/redux/questionnaires/thunk';
import {Option, Product, Relation} from '~/redux/questionnaires/types';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {getCurrentUserFromStorage} from '~/services/auth';
import {spacing} from '~/utils/theme/spacing';
import {requestOK} from '~/utils/thunk-api';

const initSelectedYesOptions = (arr: Relation[]) => {
  return arr.reduce((res: any, cur: Relation) => {
    if (cur?.key) {
      res[cur?.key] = '';
      return res;
    }
  }, {});
};

const Questionnaire7Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const {accessToken: savedToken} = getCurrentUserFromStorage();
  const {addMessage, addValidationMessage} = useNotification(dispatch);
  const [isDesktop] = useMediaQuery(`(min-width: ${QUERY_MOBILE})`);

  const {loading} = useRequestState(addQuestionnaire);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[STI_TESTING_KEYS.CHOOSE_MEDICATION]?.data,
  );

  const medicalQuestionnaireAnswer = useSelector((state: RootState) => state.questionnaires?.collectData);

  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const {wpAuthUser} = medicalQuestionnaire;

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === STI_TESTING_KEYS.CHOOSE_MEDICATION);

  const {inputs, setInputs, validation} = useInput({
    productId: currentAnswer?.productId || '',
    productIndex: currentAnswer?.productIndex || 2,
  });

  const renderSlideProduct = () => {
    return (
      <AppCarousel
        selectedItem={inputs.productIndex}
        showIndicators={false}
        showArrows={true}
        showStatus={false}
        showThumbs={false}
        onChange={val => setInputs({productIndex: val})}
      >
        {questionnaire?.data?.map((product: Product) => {
          return (
            <UiCard variant="rounded" m={spacing.small} px={spacing.small} key={product.label}>
              <UiText fontSize="xl" fontWeight="600">
                {product.label}
              </UiText>
              <UiText fontSize="xl" fontWeight="600" mt={spacing.tiny}>
                {`${product.unit}${product.price}*`}
              </UiText>
              {!!product.description && (
                <UiBox display="flex" alignItems="center" justifyContent="center">
                  <UiText>{product.description}</UiText>
                  <UiBox pt={1}>
                    <UiImage boxSize="18px" width="80px !important" url={product.paymentLogo} ml={spacing.mini} />
                  </UiBox>
                </UiBox>
              )}

              <UiText mt={spacing.tiny} color="fadedJade">
                includes:
              </UiText>
              <UiBox mt={spacing.small}>
                {questionnaire?.defineInclude?.map(val => (
                  <UiBox
                    key={`${val.value}-${val.label}`}
                    px={isDesktop ? spacing.medium : spacing.small}
                    py={spacing.tiny}
                    mr={spacing.tiny}
                    mb={spacing.tiny}
                    borderWidth="1px"
                    borderColor="baseColor"
                    borderRadius={spacing.tiny}
                    display="inline-block"
                    boxShadow="0px 4px 25px rgba(0, 0, 0, 0.1)"
                    opacity={product.includes.some(element => element === val.key) ? 1 : 0.3}
                  >
                    <UiText fontWeight="bold">{val.label}</UiText>
                  </UiBox>
                ))}
              </UiBox>
              <UiDivider mb={spacing.mini} />
              <UiSimpleGrid columns={{base: 1, md: 2}} spacing={5}>
                {product?.extended?.map(extend => (
                  <UiBox
                    key={`${extend.value}-${extend.label}`}
                    px={isDesktop ? spacing.medium : spacing.tiny}
                    py={spacing.tiny}
                    borderWidth="1px"
                    borderColor="baseColor"
                    borderRadius={spacing.tiny}
                    display="inline-block"
                    boxShadow="0px 4px 25px rgba(0, 0, 0, 0.1)"
                  >
                    <UiText fontWeight="bold">{extend.label}</UiText>
                  </UiBox>
                ))}
              </UiSimpleGrid>
            </UiCard>
          );
        })}
      </AppCarousel>
    );
  };

  const dotSlider = () => (
    <UiBox
      position="absolute"
      background="primaryBackground"
      color="primaryBackground"
      w="6px"
      h="6px"
      zIndex={1}
      bottom="1px"
    />
  );

  const onClickSubmit = async () => {
    const errors = validation.getErrors();
    if (errors) {
      addValidationMessage(errors);
      return;
    }

    const questionnaireAnswer = {
      key: STI_TESTING_KEYS.CHOOSE_MEDICATION,
      index: questionnaire?.index || '',
      data: {...inputs, productId: questionnaire?.data?.[inputs.productIndex]?.productId},
    };
    dispatch(setQuestionnaireAnswer(questionnaireAnswer));

    const resAction = await dispatch(
      addQuestionnaire({
        data: {
          data: {
            ...medicalQuestionnaireAnswer,
            [STI_TESTING_KEYS.CHOOSE_MEDICATION]: {...questionnaireAnswer},
          },
          wpUserId: wpAuthUser?.wpUserId,
          methodKey: METHOD_KEYS.STI_TESTING,
        },
      }),
    );

    if (requestOK(resAction, addQuestionnaire)) {
      const result = unwrapResult(resAction);
      if (result?.booking) {
        const type = result?.slotTime;
        dispatch(setUserQuestionnaireId({id: result?.medical?.id}));
        history.push(`/appointment?type=${type}&methodKey=${METHOD_KEYS.STI_TESTING}&token=${savedToken}`);
      }
    }
  };

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      disableNext={false}
      handleOnNext={onClickSubmit}
      loading={loading}
      checkoutStep={questionnaire?.checkoutStep}
    >
      <Col alignItems="center" textAlign="center">
        <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.small} />
        <UiText fontSize="lg" fontWeight="bold" mt={spacing.medium}>
          {questionnaire?.questions?.default}
        </UiText>
        <Slider
          defaultValue={inputs.productIndex}
          value={inputs.productIndex}
          min={0}
          max={3}
          step={1}
          h="6px"
          mt={spacing.small}
          onChange={val => setInputs({productIndex: val})}
        >
          <SliderMark value={1} mt="1" ml="-2.5" fontSize="sm">
            {dotSlider()}
          </SliderMark>
          <SliderMark value={2} mt="1" ml="-2.5" fontSize="sm">
            {dotSlider()}
          </SliderMark>
          <SliderTrack h="6px">
            <UiBox position="relative" right={10} />
            <SliderFilledTrack h="6px" />
          </SliderTrack>
          <SliderThumb boxSize={6} />
        </Slider>
        <UiBox w="100%" display="flex" alignItems="center" justifyContent="space-between">
          <UiText fontWeight="bold">
            Least <br /> comprehensive
          </UiText>
          <UiText fontWeight="bold">
            Most <br /> comprehensive
          </UiText>
        </UiBox>
      </Col>
      {renderSlideProduct()}
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire7Screen;
